import { useId, useRef } from 'react'
import PropTypes from 'prop-types'
import { AnimatePresence, motion } from 'framer-motion'

import DDIcon from '../../icons/DDIcon'
import useFocusTrap from './useFocusTrap'
import { useReducedMotionPreference, DURATION, EASE, EASE_SHEET, EASE_SHEET_CLOSE } from '../../../styles/motion'
import styles from './BottomSheet.module.css'

/**
 * Spec §5.10 — bottom sheet scoped inside the widget (covers header + footer, not a
 * document-level portal like Toast/DropdownOverlay). Tap scrim or X to close, blocked
 * while `closeBlocked` is set (e.g. a code is being checked). Spec §10: role="dialog",
 * focus trapped via useFocusTrap, Esc closes (also blocked by closeBlocked).
 */
const BottomSheet = ({ isOpen, onClose, icon, title, description, children, closeBlocked }) => {
  const sheetRef = useRef(null)
  const titleId = useId()
  const reduceMotion = useReducedMotionPreference()

  const handleClose = () => {
    if (closeBlocked) return
    onClose()
  }

  useFocusTrap(sheetRef, isOpen, handleClose)

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className={styles.scrim}
            onClick={handleClose}
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reduceMotion ? undefined : { opacity: 0 }}
            transition={{ duration: DURATION.scrimFade, ease: EASE }}
          />
          <motion.div
            ref={sheetRef}
            className={styles.sheet}
            role='dialog'
            aria-modal='true'
            aria-labelledby={titleId}
            initial={reduceMotion ? false : { y: '100%' }}
            animate={{ y: 0 }}
            exit={reduceMotion ? undefined : { y: '100%' }}
            transition={{
              duration: isOpen ? DURATION.sheetOpen : DURATION.sheetClose,
              ease: isOpen ? EASE_SHEET : EASE_SHEET_CLOSE,
            }}
          >
            <span className={styles.handle} aria-hidden='true' />
            <div className={styles.headerRow}>
              <span className={styles.iconTile}>
                <DDIcon name={icon} />
              </span>
              <button
                type='button'
                className={styles.closeButton}
                aria-label='Close'
                disabled={closeBlocked}
                onClick={handleClose}
              >
                <DDIcon name='close' />
              </button>
            </div>
            <div className={styles.titleBlock}>
              <h2 id={titleId} className={styles.title}>
                {title}
              </h2>
              {description && <p className={styles.body}>{description}</p>}
            </div>
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

BottomSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  children: PropTypes.node,
  closeBlocked: PropTypes.bool,
}

BottomSheet.defaultProps = {
  closeBlocked: false,
}

export default BottomSheet
